import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Button, Snackbar, IconButton, Typography, Box, TextField} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Letter } from "../types";
import { decode } from "../../utils/Utils";
import CloseIcon from "@mui/icons-material/Close";

type Props = {
  open: boolean;
  onClose: () => void;
  hostageId: string;
  isAdmin: boolean;
  letter?: Letter;
  resetOnOpen?: boolean;
  replyToId?: string;
};

const LetterForm = ({ open, onClose, hostageId, isAdmin, letter, replyToId, resetOnOpen = false }: Props) => {
  const { t } = useTranslation();
  const [text, setText] = useState(letter?.text || "");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");

  
  useEffect(() => {
    if (letter) {
      setText(letter.text);
      setPreviewUrl(letter.photo || "");  
    }
    else {
      setText("");
      setUploadedPhoto(null);
      setPreviewUrl("");
    }
    
  }, [letter]);


  // Handle image deletion
  const handleDeleteImage = () => {
    setUploadedPhoto(null);
    setPreviewUrl(""); // Clear the preview
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedPhoto(file);
      setPreviewUrl(URL.createObjectURL(file));  // Display the chosen file name
    } else {
      setUploadedPhoto(null);
      setPreviewUrl(""); 
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const base64Image = uploadedPhoto ? await getBase64Photo() : letter?.photo || null;

    const letterData: Letter = {
      hostage: hostageId,
      text: text,
      photo: base64Image,
      author: letter?.author,
      authorEmail: letter?.authorEmail,
      isAdmin: isAdmin,
      replyToId: replyToId
    };

    if (letter?.id) {
      await updateLetter(letter.id, letterData);
    } else {
      await createLetter(letterData);
    }
  };

  async function createLetter(letterData: Letter) {
    letterData.authorCountry = await getCountryCode();
    try {
      await fetch("/api/letter", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-HOSTAGE-ID": letterData.hostage,
        },
        body: JSON.stringify(letterData),
      });
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 4000);
    } catch (err) {
      console.error("Error creating letter:", err);
    }
  }

  async function getBase64Photo() {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event: ProgressEvent<FileReader>) => {
        resolve(event.target?.result?.toString() || "");
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(uploadedPhoto!);
    });
  }

  async function updateLetter(id: string, letterData: Letter) {
    try {
      letterData.authorCountry = await getCountryCode();
      await fetch(`/api/letter/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-HOSTAGE-ID": letterData.hostage,
        },
        body: JSON.stringify(letterData),
      });
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 4000);
    } catch (err) {
      console.error("Error updating letter:", err);
    }
  }

  async function getCountryCode() {
    const response = await fetch('https://ipapi.co/json/'); // Replace with your preferred geolocation API
    const data = await response.json();
    return data.country_code; 
  }

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ style: { borderRadius: "10px 10px 0 0" } }}
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        style={{ position: "absolute", left: 16, top: 16 }} // Position the X button
      >
        <CloseIcon />
      </IconButton>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>
          {letter ? decode(t("createLetter.editTitle")) : decode(t("createLetter.title"))}
        </Typography>
        <Box mb={3}>
          
          {!previewUrl ? (
            <Button
              variant="outlined"
              component="label"
              sx={{ textTransform: "none", fontSize: "14px" }}
            >
              {t("createLetter.addImage")}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          ) : (
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                maxWidth: "100%",
                maxHeight: "200px",
              }}
            >
              {/* Display Preview Image */}
              <img
                src={previewUrl}
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: "200px",
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              {/* Delete Button */}
              <IconButton
                aria-label="delete image"
                onClick={handleDeleteImage}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 1)" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
          </Box>
        <TextField
          id="text"
          label={t("createLetter.letterText")}
          placeholder={t("createLetter.letterTextPlaceholder")}
          multiline
          rows={4}
          fullWidth
          required
          value={text}
          onChange={(e) => setText(e.target.value)}
          sx={{ marginBottom: "24px" }}
        />

        <Button className="submit-button" type="submit">
          {t("createLetter.submit")}
        </Button>
      </form>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={letter ? t("createLetter.successMessage") : t("createLetter.successMessage")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Drawer>
  );
};

export default LetterForm;
